import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reportsService, attendanceService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import {
  FileText,
  Download,
  Calendar,
  Clock,
  Users,
  TrendingUp,
  Loader2,
  Filter,
  Edit2,
} from 'lucide-react';

const DEPARTMENTS = [
  'Engineering', 'Product', 'Design', 'Marketing', 'Sales',
  'HR', 'Finance', 'Operations', 'Support', 'Legal'
];

const escapeCSVValue = (value) => {
  if (value === null || value === undefined) {
    return '""';
  }

  const normalized = String(value).replace(/"/g, '""');
  return `"${normalized}"`;
};

const humanizeValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value).replace(/_/g, ' ');
};

const ATTENDANCE_EXPORT_COLUMNS = [
  { label: 'Employee', value: (row) => row.employee_name || '' },
  { label: 'Employee Code', value: (row) => row.employee_code || '' },
  { label: 'Department', value: (row) => row.department || '' },
  { label: 'Date', value: (row) => row.date ? format(new Date(row.date), 'yyyy-MM-dd') : '' },
  { label: 'Check In', value: (row) => row.check_in_time ? format(new Date(row.check_in_time), 'HH:mm') : '' },
  { label: 'Check Out', value: (row) => row.check_out_time ? format(new Date(row.check_out_time), 'HH:mm') : '' },
  { label: 'Location', value: (row) => row.location_name || row.location_id || '' },
  { label: 'Working Hours', value: (row) => row.working_hours ?? '' },
  { label: 'Status', value: (row) => humanizeValue(row.status) },
  { label: 'Attendance Type', value: (row) => humanizeValue(row.attendance_type) },
  { label: 'Late', value: (row) => row.is_late ? 'Yes' : 'No' },
  { label: 'Late Minutes', value: (row) => row.late_minutes ?? '' },
];

const LATE_REPORT_EXPORT_COLUMNS = [
  { label: 'Employee', value: (row) => row.employee_name || '' },
  { label: 'Department', value: (row) => row.department || '' },
  { label: 'Late Days', value: (row) => row.total_late_days ?? '' },
  { label: 'Total Late Minutes', value: (row) => row.total_late_minutes ?? '' },
  { label: 'Average Late Minutes', value: (row) => row.avg_late_minutes ?? '' },
];

const LEAVE_REPORT_EXPORT_COLUMNS = [
  { label: 'Employee', value: (row) => row.employee_name || '' },
  { label: 'Department', value: (row) => row.department || '' },
  { label: 'Earned Used', value: (row) => row.earned_used ?? '' },
  { label: 'Earned Total', value: (row) => row.earned_total ?? '' },
  { label: 'Earned Balance', value: (row) => row.earned_balance ?? '' },
  { label: 'Sick Used', value: (row) => row.sick_used ?? '' },
  { label: 'Sick Total', value: (row) => row.sick_total ?? '' },
  { label: 'Sick Balance', value: (row) => row.sick_balance ?? '' },
  { label: 'Comp Off Used', value: (row) => row.comp_off_used ?? '' },
  { label: 'Comp Off Total', value: (row) => row.comp_off_total ?? '' },
  { label: 'Comp Off Balance', value: (row) => row.comp_off_balance ?? '' },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [attendanceReport, setAttendanceReport] = useState([]);
  const [lateReport, setLateReport] = useState([]);
  const [leaveReport, setLeaveReport] = useState([]);

  const [filters, setFilters] = useState({
    start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    department: 'all',
    year: new Date().getFullYear(),
  });

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState({
    check_in: '',
    check_out: '',
    reason: '',
  });

  const isHRUser = () => user?.role === 'hr' || user?.role === 'admin' || user?.role === 'super_admin';

  const loadAttendanceReport = async () => {
    setLoading(true);
    try {
      const data = await reportsService.getAttendanceReport(
        filters.start_date,
        filters.end_date,
        filters.department === 'all' ? null : filters.department
      );
      setAttendanceReport(data);
    } catch (error) {
      toast.error('Failed to load attendance report');
    } finally {
      setLoading(false);
    }
  };

  const loadLateReport = async () => {
    setLoading(true);
    try {
      const data = await reportsService.getLateArrivalsReport(
        filters.start_date,
        filters.end_date
      );
      setLateReport(data);
    } catch (error) {
      toast.error('Failed to load late arrivals report');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaveReport = async () => {
    setLoading(true);
    try {
      const data = await reportsService.getLeaveSummaryReport(filters.year);
      setLeaveReport(data);
    } catch (error) {
      toast.error('Failed to load leave report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename, columns) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = columns.map((column) => escapeCSVValue(column.label)).join(',');
    const rows = data
      .map((row) => columns.map((column) => escapeCSVValue(column.value(row))).join(','))
      .join('\n');
    const csv = `${headers}\n${rows}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success('Report exported successfully!');
  };

  const handleEditClick = (record) => {
    setSelectedRecord(record);
    setEditData({
      check_in: record.check_in_time ? format(new Date(record.check_in_time), "yyyy-MM-dd'T'HH:mm") : '',
      check_out: record.check_out_time ? format(new Date(record.check_out_time), "yyyy-MM-dd'T'HH:mm") : '',
      reason: '',
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async () => {
    if (!editData.reason.trim()) {
      toast.error('Please provide a reason for editing');
      return;
    }

    setEditLoading(true);
    try {
      await attendanceService.edit(selectedRecord.id, {
        check_in: editData.check_in || null,
        check_out: editData.check_out || null,
        reason: editData.reason,
      });
      toast.success('Attendance updated successfully');
      setShowEditDialog(false);
      loadAttendanceReport();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail :
        Array.isArray(detail) ? detail[0]?.msg :
          'Failed to update attendance';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">
          Generate and export attendance, leave, and analytics reports
        </p>
      </div>

      <Tabs defaultValue="attendance" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="attendance" data-testid="tab-attendance-report">
            <Clock className="w-4 h-4 mr-2" />
            Attendance
          </TabsTrigger>
          <TabsTrigger value="late" data-testid="tab-late-report">
            <TrendingUp className="w-4 h-4 mr-2" />
            Late Arrivals
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave-report">
            <Calendar className="w-4 h-4 mr-2" />
            Leave Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="attendance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Attendance Report</CardTitle>
              <CardDescription>
                View detailed attendance records for the selected period
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select
                    value={filters.department}
                    onValueChange={(v) => setFilters({ ...filters, department: v })}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {DEPARTMENTS.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadAttendanceReport} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
                  Generate
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportToCSV(attendanceReport, 'attendance_report', ATTENDANCE_EXPORT_COLUMNS)}
                  disabled={attendanceReport.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>

              {attendanceReport.length > 0 && (
                <div className="border rounded-lg overflow-auto max-h-96">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead className="hidden md:table-cell">Code</TableHead>
                        <TableHead className="hidden md:table-cell">Department</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Check In</TableHead>
                        <TableHead className="hidden sm:table-cell">Check Out</TableHead>
                        <TableHead className="hidden lg:table-cell">Location</TableHead>
                        <TableHead className="hidden sm:table-cell">Hours</TableHead>
                        <TableHead>Status</TableHead>
                        {isHRUser() && <TableHead>Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attendanceReport.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{record.employee_name}</TableCell>
                          <TableCell className="hidden md:table-cell font-mono text-sm">{record.employee_code}</TableCell>
                          <TableCell className="hidden md:table-cell">{record.department}</TableCell>
                          <TableCell>{format(new Date(record.date), 'MMM dd')}</TableCell>
                          <TableCell className="font-mono">
                            {record.check_in_time ? format(new Date(record.check_in_time), 'HH:mm') : '-'}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell font-mono">
                            {record.check_out_time ? format(new Date(record.check_out_time), 'HH:mm') : '-'}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">{record.location_name || record.location_id || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell">{record.working_hours || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`capitalize ${record.status === 'checked_out' ? 'border-green-500/50 text-green-400' :
                                record.status === 'checked_in' ? 'border-blue-500/50 text-blue-400' :
                                  'border-red-500/50 text-red-400'
                                }`}>
                                {record.status}
                              </Badge>
                              {record.is_edited && (
                                <Badge variant="secondary" className="text-[10px] py-0">Edited</Badge>
                              )}
                            </div>
                          </TableCell>
                          {isHRUser() && (
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(record)}
                                title="Edit Attendance"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {attendanceReport.length === 0 && !loading && (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  Click "Generate" to view the report
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="late" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Late Arrivals Report</CardTitle>
              <CardDescription>
                Employees with frequent late arrivals in the selected period
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
                <Button onClick={loadLateReport} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
                  Generate
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportToCSV(lateReport, 'late_arrivals_report', LATE_REPORT_EXPORT_COLUMNS)}
                  disabled={lateReport.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>

              {lateReport.length > 0 && (
                <div className="border rounded-lg overflow-auto max-h-96">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead className="hidden sm:table-cell">Department</TableHead>
                        <TableHead>Late Days</TableHead>
                        <TableHead className="hidden sm:table-cell">Total Late Mins</TableHead>
                        <TableHead className="hidden md:table-cell">Avg Late (mins)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lateReport.map((record) => (
                        <TableRow key={record._id || record.employee_id}>
                          <TableCell className="font-medium">{record.employee_name}</TableCell>
                          <TableCell className="hidden sm:table-cell">{record.department}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="border-orange-500/50 text-orange-400">
                              {record.total_late_days}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">{Math.round(record.total_late_minutes)}</TableCell>
                          <TableCell className="hidden md:table-cell">{Math.round(record.avg_late_minutes)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {lateReport.length === 0 && !loading && (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  Click "Generate" to view the report
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leave" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Leave Summary Report</CardTitle>
              <CardDescription>
                Employee leave balances and usage for the selected year
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select
                    value={filters.year.toString()}
                    onValueChange={(v) => setFilters({ ...filters, year: parseInt(v) })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadLeaveReport} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
                  Generate
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportToCSV(leaveReport, 'leave_summary_report', LEAVE_REPORT_EXPORT_COLUMNS)}
                  disabled={leaveReport.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>

              {leaveReport.length > 0 && (
                <div className="border rounded-lg overflow-auto max-h-96">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead className="hidden md:table-cell">Department</TableHead>
                        <TableHead className="hidden sm:table-cell">Casual</TableHead>
                        <TableHead className="hidden sm:table-cell">Sick</TableHead>
                        <TableHead>Earned</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leaveReport.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{record.employee_name}</TableCell>
                          <TableCell className="hidden md:table-cell">{record.department}</TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <span className="text-blue-400">{record.casual_used}</span>
                            <span className="text-muted-foreground text-xs"> / {record.casual_total}</span>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <span className="text-red-400">{record.sick_used}</span>
                            <span className="text-muted-foreground text-xs"> / {record.sick_total}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-green-400">{record.earned_used}</span>
                            <span className="text-muted-foreground text-xs"> / {record.earned_total}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {leaveReport.length === 0 && !loading && (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  Click "Generate" to view the report
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Attendance Modal */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Attendance Log</DialogTitle>
            <DialogDescription>
              Correct attendance for {selectedRecord?.employee_name} on {selectedRecord?.date}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="check_in">Check In Time</Label>
              <Input
                id="check_in"
                type="datetime-local"
                value={editData.check_in}
                onChange={(e) => setEditData({ ...editData, check_in: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="check_out">Check Out Time</Label>
              <Input
                id="check_out"
                type="datetime-local"
                value={editData.check_out}
                onChange={(e) => setEditData({ ...editData, check_out: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reason">Reason for Adjustment *</Label>
              <Textarea
                id="reason"
                placeholder="Selfie failure, network issue, forgot to punch, etc."
                value={editData.reason}
                onChange={(e) => setEditData({ ...editData, reason: e.target.value })}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={editLoading}>
              {editLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
